import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { userByIdQueryAtom } from "../services/queries";
import { currentUserState } from "../state";
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  authenticate,
  fetchAuthenticatedUser,
  fetchUsersWithMetaParams,
} from "../services/users";

export const useGetUsers = () => {
  return useQuery({
    queryKey: ["users"],
    queryFn: fetchUsers,
    refetchOnWindowFocus: false,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useAuthenticateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authenticate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users/authenticate"] });
    },
  });
};

export const useAuthenticatedUser = () => {
  return useQuery({
    queryKey: ["users/me"],
    queryFn: fetchAuthenticatedUser,
    refetchOnWindowFocus: false,
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

// Custom hook for managing edit page state based on URL params
export const useEditUserSyncPathParamAtom = () => {
  const { uid: userId } = useParams();

  // const [currentCondition, setCurrentCondition] = useAtomWithQuery();
  const navigate = useNavigate();

  const userIdAtom = useMemo(() => userByIdQueryAtom(userId), [userId]);

  const [{ data: currentUserData, isPending, isError }] = useAtom(userIdAtom, {
    delay: 1, // Delay to avoid immediate loading state
    refetchOnWindowFocus: false, // Avoid refetching on window focus
  });

  const [currentUserRecord, setCurrentUserRecord] = useAtom(currentUserState, {
    delay: 1,
  });


  // on mount & when URL changes, push into atom
  useEffect(() => {
    if (userId != null) {
      // If userId is present
      setCurrentUserRecord(currentUserData);
    }
  }, [userId, currentUserData, setCurrentUserRecord]);

  // on unmount, clear the atom
  useEffect(() => {
    return () => {
      setCurrentUserRecord(null);
    };
  }, [setCurrentUserRecord]);

  // If userId is not present, navigate to the users list page
  useEffect(() => {
    if (!userId) {
      navigate("/users");
    }
  }, [userId, navigate]);

  return [currentUserRecord, setCurrentUserRecord];
};

export const useGetUsersWithParams = (params) => {
  return useQuery({
    queryKey: ["usersWithMeta", params],
    queryFn: () => fetchUsersWithMetaParams(params),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    enabled: true,
  });
};

export const useGetUsersWithParamsStatic = (params) => {
  return useQuery({
    queryKey: ["usersWithMeta", {params}],
    queryFn: () => fetchUsersWithMetaParams(params),
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    enabled: false,
  });
};
